/**
 * ObstructionAffectedRoadsSection
 *
 * Read-only "Affected roads" block of the obstruction overview. Renders a
 * heading plus one row per additional affected road — `location` prominent,
 * `description` muted (only when present). Mirrors the visual language of the
 * basic-info grid (`infoValue` style) rather than introducing new chrome.
 */
import { memo } from 'react';
import { Box, Group, Input, Stack, Text } from '@mantine/core';
import { IconRoad } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { ICON_SIZE_TAB } from '@/constants/ui';
import type { ObstructionAffectedRoad } from '@/types/obstructions';

import styles from '../ObstructionGeneralInfo.module.css';

interface ObstructionAffectedRoadsSectionProps {
  affectedRoads: ObstructionAffectedRoad[];
}

export const ObstructionAffectedRoadsSection = memo(
  function ObstructionAffectedRoadsSection({
    affectedRoads,
  }: ObstructionAffectedRoadsSectionProps) {
    const { t } = useTranslation();

    return (
      <Box className={styles.section}>
        <Group gap="xs" align="center">
          <IconRoad size={ICON_SIZE_TAB} />
          <Input.Label>
            {t('obstructions.detail.fields.affectedRoads')}
          </Input.Label>
        </Group>
        <Stack gap="xs" className={styles.affectedRoadsList}>
          {affectedRoads.map((affectedRoad) => (
            <Box key={affectedRoad.id} className={styles.affectedRoadRow}>
              <Text className={styles.affectedRoadLocation}>
                {affectedRoad.location}
              </Text>
              {affectedRoad.description !== null &&
                affectedRoad.description.length > 0 && (
                  <Text size="sm" c="dimmed" className={styles.infoValue}>
                    {affectedRoad.description}
                  </Text>
                )}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }
);
